import type { Element, ElementPatch, Page } from '@/components/canvas/document/publicTypes';
import { elementToWorldElement } from './unionBounds';

export interface MultiSelectionSnapshot {
  id: string;
  type: string;
  pageId?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  endX?: number;
  endY?: number;
}

export function buildDocumentSnapshots(
  pages: Page[],
  workspaceElements: Element[],
  selectedIds: string[],
): MultiSelectionSnapshot[] {
  const idSet = new Set(selectedIds);
  const snaps: MultiSelectionSnapshot[] = [];

  for (const page of pages) {
    for (const el of page.elements) {
      if (!idSet.has(el.id) || el.type === 'line') continue;
      snaps.push({
        id: el.id,
        type: el.type,
        pageId: page.id,
        x: el.layout.x,
        y: el.layout.y,
        width: el.size.width,
        height: el.size.height,
      });
    }
  }

  for (const el of workspaceElements) {
    if (!idSet.has(el.id) || el.type === 'line') continue;
    snaps.push({
      id: el.id,
      type: el.type,
      x: el.layout.x,
      y: el.layout.y,
      width: el.size.width,
      height: el.size.height,
    });
  }

  return snaps;
}

export function snapshotToWorld(
  snap: MultiSelectionSnapshot,
  pages: Page[],
): { x: number; y: number; width: number; height: number } {
  if (!snap.pageId) {
    return { x: snap.x, y: snap.y, width: snap.width, height: snap.height };
  }
  const page = pages.find((p) => p.id === snap.pageId);
  if (!page) return { x: snap.x, y: snap.y, width: snap.width, height: snap.height };
  const elStub = {
    id: snap.id,
    type: snap.type,
    layout: { x: snap.x, y: snap.y, rotation: 0 },
    size: { width: snap.width, height: snap.height },
  } as Element;
  const world = elementToWorldElement(elStub, page.workspaceLayout.x, page.workspaceLayout.y);
  return {
    x: world.layout.x,
    y: world.layout.y,
    width: world.size.width,
    height: world.size.height,
  };
}

export function unionBoundsFromSnapshots(
  snapshots: MultiSelectionSnapshot[],
  pages: Page[],
): { x: number; y: number; width: number; height: number } | null {
  const rects = snapshots.map((s) => {
    const w = snapshotToWorld(s, pages);
    return { x: w.x, y: w.y, width: w.width, height: w.height };
  });
  if (rects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export type MultiSelectionCoordinateSpace = 'page' | 'world';

function snapshotRectInSpace(
  snap: MultiSelectionSnapshot,
  pages: Page[],
  space: MultiSelectionCoordinateSpace,
): { x: number; y: number; width: number; height: number } {
  if (space === 'page' && snap.pageId) {
    return { x: snap.x, y: snap.y, width: snap.width, height: snap.height };
  }
  return snapshotToWorld(snap, pages);
}

/** 相对联合框原点缩放（与 useGroupResize 一致，锚点为 union 左上角） */
export function scaleSnapshotToPatch(
  snap: MultiSelectionSnapshot,
  pages: Page[],
  unionStart: { x: number; y: number },
  unionNew: { x: number; y: number },
  scaleX: number,
  scaleY: number,
  space: MultiSelectionCoordinateSpace,
): ElementPatch {
  const { x, y, width, height } = snapshotRectInSpace(snap, pages, space);
  const relX = x - unionStart.x;
  const relY = y - unionStart.y;
  const scaledX = unionNew.x + relX * scaleX;
  const scaledY = unionNew.y + relY * scaleY;
  const newW = Math.max(1, width * scaleX);
  const newH = Math.max(1, height * scaleY);

  let layoutX = scaledX;
  let layoutY = scaledY;
  if (space === 'world' && snap.pageId) {
    const page = pages.find((p) => p.id === snap.pageId);
    if (page) {
      layoutX = scaledX - page.workspaceLayout.x;
      layoutY = scaledY - page.workspaceLayout.y;
    }
  }

  const patch: ElementPatch = {
    layout: { x: layoutX, y: layoutY },
    size: { width: newW, height: newH },
  };
  if (snap.type === 'line') {
    patch.data = {
      end: {
        x: (snap.endX ?? snap.width) * scaleX,
        y: (snap.endY ?? 0) * scaleY,
      },
    };
  }
  return patch;
}
